import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillButton } from '../../components/site/Pill';
import { useSchemes, SchemeCard } from '../../components/site/SchemeGrid';
import { openSiteChat } from '../../components/SiteChat';
import { schemeImage, INTERIORS } from '../../lib/site';
import { ListingCard, type Listing } from './ListingCard';
import type { ListMeta } from '../../types/api';

const BUDGETS = [1200, 1500, 1800, 2200, 2800];

/**
 * Locations. On lotusliving.co.uk this page is a list of schemes with an enquiry form;
 * here it is the live homes-to-rent search — the same searchListings service the staff
 * page and the AI tool use, so a home that lets this morning disappears this morning.
 */
export default function SiteListings() {
  const [params, setParams] = useSearchParams();
  const scheme = params.get('scheme') ?? '';
  const [filters, setFilters] = useState<Record<string, string>>({ city: params.get('city') ?? '', bedrooms: '', maxRent: '', availableFrom: '', q: '' });
  const [page, setPage] = useState(1);

  const schemes = useSchemes();
  const current = schemes.data?.find((s) => s.slug === scheme);

  const query = useMemo(() => Object.fromEntries(Object.entries({ ...filters, propertySlug: scheme || undefined, page, perPage: 12, sort: 'rent' }).filter(([, v]) => v !== '' && v !== undefined)), [filters, scheme, page]);
  const { data, isLoading } = useQuery({ queryKey: ['public-listings', query], queryFn: async () => api.public.get<Listing[]>('/public/listings', query) });
  const meta = data?.meta as ListMeta | undefined;

  const set = (k: string, v: string) => { setPage(1); setFilters({ ...filters, [k]: v }); };
  const clearScheme = () => { params.delete('scheme'); params.delete('soon'); setParams(params); setPage(1); };
  const cities = useMemo(() => Array.from(new Set((schemes.data ?? []).map((s) => s.city).filter(Boolean))).sort() as string[], [schemes.data]);
  const active = Object.values(filters).some(Boolean) || !!scheme;

  return (
    <SiteLayout>
      {/* Scheme header when arriving from a scheme card */}
      {current ? (
        <section className="relative overflow-hidden bg-ink-900 text-white">
          <img src={schemeImage(current.slug)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900/90 via-ink-900/60 to-ink-900/20" />
          <div className="site-container relative z-10 py-20 lg:py-28">
            <button onClick={clearScheme} className="mb-6 inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.18em] text-white/70 hover:text-white"><X className="h-3.5 w-3.5" /> All locations</button>
            <p className="eyebrow !text-bronze-300">{current.city}{current.region === 'AE' ? ' · Dubai' : ''}</p>
            <h1 className="display mt-3 text-[48px] sm:text-[64px] lg:text-[80px]">{current.name}</h1>
            {current.description && <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-white/80">{current.description}</p>}
            {!!current.amenities?.length && (
              <ul className="mt-6 flex flex-wrap gap-2">
                {current.amenities.map((a) => <li key={a} className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[12px] text-white/90"><Check className="h-3 w-3 text-bronze-300" />{a}</li>)}
              </ul>
            )}
          </div>
        </section>
      ) : (
        <section className="site-container pt-16 lg:pt-24">
          <p className="eyebrow">Locations</p>
          <h1 className="display mt-4 max-w-[900px] text-[48px] text-ink-900 sm:text-[64px] lg:text-[84px]"><em>Discover</em> our Locations</h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-ink-500">Every home below is available to rent right now. No enquiry form: pick a home, apply online, and our lettings team will be in touch within one working day.</p>
        </section>
      )}

      {/* Filter bar */}
      <section className="site-container mt-12">
        <div className="flex flex-wrap items-center gap-2 rounded-full border border-ink-200 bg-white p-2 shadow-card">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input value={filters.q} onChange={(e) => set('q', e.target.value)} placeholder="Search area, scheme or feature" className="w-full rounded-full bg-transparent py-2.5 pl-11 pr-4 text-sm text-ink-900 outline-none placeholder:text-ink-400" />
          </div>
          {!scheme && (
            <Sel value={filters.city} onChange={(v) => set('city', v)}>
              <option value="">Anywhere</option>{cities.map((c) => <option key={c}>{c}</option>)}
            </Sel>
          )}
          <Sel value={filters.bedrooms} onChange={(v) => set('bedrooms', v)}>
            <option value="">Any size</option><option value="0">Studio</option><option value="1">1 bedroom</option><option value="2">2 bedrooms</option><option value="3">3 bedrooms</option>
          </Sel>
          <Sel value={filters.maxRent} onChange={(v) => set('maxRent', v)}>
            <option value="">Any budget</option>{BUDGETS.map((r) => <option key={r} value={r}>Up to £{r.toLocaleString()} pcm</option>)}
          </Sel>
          <input type="date" value={filters.availableFrom} onChange={(e) => set('availableFrom', e.target.value)} title="Move in by" className="rounded-full border border-ink-200 bg-ink-50 px-4 py-2.5 text-[13px] text-ink-700 outline-none focus:border-bronze-400" />
          {active && <button onClick={() => { setFilters({ city: '', bedrooms: '', maxRent: '', availableFrom: '', q: '' }); clearScheme(); }} className="ml-auto rounded-full px-4 py-2 text-[12px] font-medium text-ink-500 hover:bg-ink-50 hover:text-ink-900">Clear</button>}
        </div>
        <div className="mt-5 flex items-center justify-between text-[13px] text-ink-500">
          <span>{meta ? `${meta.total} home${meta.total === 1 ? '' : 's'} available${current ? ` at ${current.name}` : ''}` : ' '}</span>
          <button onClick={openSiteChat} className="copy-link font-medium">Ask our assistant instead</button>
        </div>
      </section>

      {/* Results */}
      <section className="site-container mt-8 pb-24 lg:pb-32">
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-[10px] bg-ink-100" />)
            : data?.data.length
              ? data.data.map((l, i) => <ListingCard key={l.id} listing={l} index={i} image={cardImage(data.data, i)} />)
              : (
                <div className="col-span-full rounded-[10px] border border-dashed border-ink-200 py-20 text-center">
                  <p className="display text-3xl text-ink-900">{current?.comingSoon ? 'Coming soon' : 'Nothing matches just now'}</p>
                  <p className="mx-auto mt-3 max-w-md text-sm text-ink-500">
                    {current?.comingSoon
                      ? `${current.name} is still being built. Our assistant can register your interest and tell you what is available nearby today.`
                      : 'Try widening your search, or ask our assistant — it can tell you what is coming up before it is listed.'}
                  </p>
                  <PillButton onClick={openSiteChat} variant="ghost" className="mt-8">Talk to the assistant</PillButton>
                </div>
              )}
        </div>
        {meta && meta.totalPages > 1 && (
          <div className="mt-14 flex justify-center gap-2">
            {Array.from({ length: meta.totalPages }).map((_, i) => (
              <button key={i} onClick={() => { setPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`h-10 w-10 rounded-full text-sm transition ${page === i + 1 ? 'bg-ink-900 text-white' : 'border border-ink-200 text-ink-600 hover:border-ink-900'}`}>{i + 1}</button>
            ))}
          </div>
        )}
      </section>

      {/* Other schemes */}
      {!!schemes.data?.length && (
        <section className="border-t border-ink-100 bg-cream">
          <div className="site-container py-24 lg:py-32">
            <h2 className="display text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]">{current ? <><em>More</em> locations</> : <><em>Our</em> schemes</>}</h2>
            <div className="mt-12 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
              {schemes.data.filter((s) => s.slug !== scheme).slice(0, 8).map((s, i) => <SchemeCard key={s.id} scheme={s} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}

/** The first home at a scheme shows the building; the rest cycle through interiors so a page of one scheme is not one photo nine times. */
const INTERIOR_CYCLE = [INTERIORS.livingArea, INTERIORS.bedroom, INTERIORS.kitchen, INTERIORS.harlow, INTERIORS.kitchenDetail, INTERIORS.bathroom];
function cardImage(rows: Listing[], i: number): string | undefined {
  const slug = rows[i]?.property?.slug;
  const priorSame = rows.slice(0, i).filter((r) => r.property?.slug === slug).length;
  return priorSame === 0 ? undefined : INTERIOR_CYCLE[(priorSame - 1) % INTERIOR_CYCLE.length];
}

function Sel({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="rounded-full border border-ink-200 bg-ink-50 px-4 py-2.5 text-[13px] text-ink-700 outline-none focus:border-bronze-400">
      {children}
    </select>
  );
}
